import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import type { OpsInstanceListRow } from '@/api/operations-resources';
import { formatDate } from '@/lib/format';
import { EntityLink } from '@/components/entity-link';

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'neutral';

/** Instance status → StatusPill tone (models/instance.go statuses). */
const STATUS_TONE: Record<string, Tone> = {
  running: 'success',
  stopped: 'muted',
  pending: 'warning',
  error: 'danger',
  unknown: 'neutral',
};

/**
 * DataTable columns for the cross-org Instances inventory. The Organization
 * cell links to the org detail; the row itself is not clickable (no per-instance
 * detail page in Phase 1).
 */
export const opsInstanceColumns: ColumnDef<OpsInstanceListRow, unknown>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Instance',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)', fontWeight: 'var(--vn-font-weight-medium)' }}>
        {row.original.name || '—'}
      </span>
    ),
  },
  {
    id: 'org',
    accessorKey: 'organization_id',
    header: 'Customer',
    cell: ({ row }) => {
      const { organization_id, customer } = row.original;
      return (
        <EntityLink
          to={`/operations/orgs/${organization_id}`}
          id={organization_id}
          name={customer?.customer_company_name || customer?.customer_name}
        />
      );
    },
  },
  {
    id: 'provider',
    accessorKey: 'provider_name',
    header: 'Provider',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.provider_name || '—'}</span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status || 'unknown';
      const tone = STATUS_TONE[status] ?? 'neutral';
      return (
        <StatusPill tone={tone} tooltip={status}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </StatusPill>
      );
    },
  },
  {
    id: 'uid',
    accessorKey: 'uid',
    header: 'UID',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {row.original.uid || '—'}
      </span>
    ),
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];

/** Column ids toggleable via Display options. */
export const OPS_INSTANCE_TOGGLEABLE_COLUMN_IDS = ['uid', 'created_at'];
